import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Button } from '@pwc/appkit-react';
import { useSelector, useDispatch } from 'react-redux';

import { fetchSchemasForTaxYear, fetchSchemasCompareReport } from './store/actions';

import { taxYearOptionsSelector } from '../../src/development/store/selectors';
import SDKCustomSelect from '../shared/forms/sdkCustomSelect/sdkCustomSelect.component';
import LoadingSpinner from '../shared/forms/loadingSpinner/loadingSpinner.component';
import styles from './schemasCompare.module.scss';

const SchemasCompare = () => {
  const dispatch = useDispatch();

  //For saving selected params
  const [selectedTaxYearForNewSchema, setSelectedTaxYearForNewSchema] = useState(null);
  const [selectedTaxYearForOldSchema, setSelectedTaxYearForOldSchema] = useState(null);
  const [selectedNewSchema, setSelectedNewSchema] = useState(null);
  const [selectedOldSchema, setSelectedOldSchema] = useState(null);

  //For showing the params lists in dropdowns
  const taxYears = useSelector(taxYearOptionsSelector);
  const [filteredTaxYearsForOldSchema, setFilteredTaxYearsForOldSchema] = useState([]);
  const [schemasForNewTaxYear, setSchemasForNewTaxYear] = useState([]);
  const [schemasForOldTaxYear, setSchemasForOldTaxYear] = useState([]);

  //For showing loading animations and disabling options
  const [isLoadingNewSchemas, setIsLoadingNewSchemas] = useState(false);
  const [isLoadingOldSchemas, setIsLoadingOldSchemas] = useState(false);
  const [isRunningReport, setIsRunningReport] = useState(false);
  const areParamsReady =
    selectedTaxYearForNewSchema &&
    selectedNewSchema &&
    selectedTaxYearForOldSchema &&
    selectedOldSchema;

  const fetchSchemasForNewTaxYear = async selectedTaxYearForNewSchema => {
    const taxYear = selectedTaxYearForNewSchema.value;
    const schemasForTaxYearResponse = await dispatch(fetchSchemasForTaxYear({ taxYear }));
    setSchemasForNewTaxYear(schemasForTaxYearResponse);
  };

  const fetchSchemasForOldTaxYear = async selectedTaxYearForOldSchema => {
    const taxYear = selectedTaxYearForOldSchema.value;
    const schemasForTaxYearResponse = await dispatch(fetchSchemasForTaxYear({ taxYear }));
    setSchemasForOldTaxYear(schemasForTaxYearResponse);
    setIsLoadingNewSchemas(false);
  };

  const handleNewTaxYearChange = async selectedOption => {
    setIsLoadingNewSchemas(true);
    setSelectedTaxYearForNewSchema(selectedOption);
    setSelectedNewSchema(null);
    await fetchSchemasForNewTaxYear(selectedOption);
    setIsLoadingNewSchemas(false);
  };

  const handleOldTaxYearChange = async selectedOption => {
    setIsLoadingOldSchemas(true);
    setSelectedTaxYearForOldSchema(selectedOption);
    await fetchSchemasForOldTaxYear(selectedOption);
    setIsLoadingOldSchemas(false);
  };

  const handleNewSchemaChange = selectedOption => {
    setSelectedNewSchema(selectedOption);
  };

  const handledOldSchemaChange = selectedOption => {
    setSelectedOldSchema(selectedOption);
  };

  const handleRunReport = async () => {
    setIsRunningReport(true);
    await dispatch(
      fetchSchemasCompareReport({
        newTaxYear: selectedTaxYearForNewSchema.value,
        oldTaxYear: selectedTaxYearForOldSchema.value,
        selectedNewSchema: selectedNewSchema,
        selectedOldSchema: selectedOldSchema,
      }),
    );
    setIsRunningReport(false);
  };

  useEffect(() => {
    if (selectedTaxYearForNewSchema) {
      const filteredYears = taxYears.filter(year => year.value < selectedTaxYearForNewSchema.value);
      setFilteredTaxYearsForOldSchema(filteredYears);

      if (
        selectedTaxYearForOldSchema &&
        selectedTaxYearForOldSchema.value >= selectedTaxYearForNewSchema.value
      ) {
        setSelectedTaxYearForOldSchema(null);
        setSelectedOldSchema(null);
      }
    } else {
      setFilteredTaxYearsForOldSchema(taxYears);
    }
  }, [selectedTaxYearForNewSchema, selectedTaxYearForOldSchema, taxYears]);

  return (
    <>
      <div className={styles.formListDropdownWrapper}>
        <div className={styles.formListDropdown}>
          <SDKCustomSelect
            className="sdk-custom-select"
            options={taxYears}
            onChange={handleNewTaxYearChange}
            value={selectedTaxYearForNewSchema}
            appkitLabel="Tax Year For New Schema"
          ></SDKCustomSelect>
        </div>
        <div className={styles.formListDropdown}>
          <SDKCustomSelect
            className="sdk-custom-select"
            options={schemasForNewTaxYear.map(schema => ({
              label: schema.SLT_SCHEMA_ID,
              value: schema.SLT_SCHEMA_ID,
              key: schema.SCHEMA_SET_ID,
            }))}
            onChange={handleNewSchemaChange}
            value={selectedNewSchema}
            isLoading={isLoadingNewSchemas}
            disabled={isLoadingNewSchemas}
            appkitLabel="New Schema"
          ></SDKCustomSelect>
        </div>
        <div className={styles.formListDropdown}>
          <SDKCustomSelect
            className="sdk-custom-select"
            options={filteredTaxYearsForOldSchema}
            onChange={handleOldTaxYearChange}
            value={selectedTaxYearForOldSchema}
            appkitLabel="Tax Year For Old Schema"
          ></SDKCustomSelect>
        </div>
        <div className={styles.formListDropdown}>
          <SDKCustomSelect
            className="sdk-custom-select"
            options={schemasForOldTaxYear.map(schema => ({
              label: schema.SLT_SCHEMA_ID,
              value: schema.SLT_SCHEMA_ID,
              key: schema.SCHEMA_SET_ID,
            }))}
            onChange={handledOldSchemaChange}
            value={selectedOldSchema}
            isLoading={isLoadingOldSchemas}
            disabled={isLoadingOldSchemas}
            appkitLabel="Old Schema"
          ></SDKCustomSelect>
        </div>
        <div className={styles.runReportButton}>
          <LoadingSpinner isLoading={false} />
          <Button
            size="lg"
            onClick={handleRunReport}
            isLoading={isRunningReport}
            disabled={!areParamsReady}
          >
            Run Report
          </Button>
        </div>
      </div>
    </>
  );
};

export default SchemasCompare;
